/**
 * FilmIQ Platform v3 - Chat History Service
 * 
 * Persists chat history per user in Firebase Firestore.
 * Each user has their own chat history document.
 */

import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { ChatMessage } from '../components/copilot/copilotApi';

const COLLECTION_NAME = 'chatHistory';
const MAX_MESSAGES = 100; // Keep last 100 messages per user

interface ChatHistoryDoc {
  messages: ChatMessage[];
  updatedAt: Timestamp;
  userId: string;
  userEmail: string;
}

/**
 * Load chat history for a specific user
 */
export async function loadChatHistory(userId: string): Promise<ChatMessage[]> {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as ChatHistoryDoc;
      return data.messages || [];
    }
    
    return [];
  } catch (error) {
    console.error('Error loading chat history:', error);
    return [];
  }
}

/**
 * Save chat history for a specific user
 */
export async function saveChatHistory(
  userId: string, 
  userEmail: string,
  messages: ChatMessage[]
): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    
    // Keep only the last MAX_MESSAGES
    const trimmedMessages = messages.slice(-MAX_MESSAGES);
    
    await setDoc(docRef, {
      messages: trimmedMessages,
      updatedAt: serverTimestamp(),
      userId,
      userEmail,
    }, { merge: true });
  } catch (error) {
    console.error('Error saving chat history:', error);
  }
}

/**
 * Clear chat history for a specific user
 */
export async function clearChatHistory(userId: string): Promise<void> {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await setDoc(docRef, {
      messages: [],
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.error('Error clearing chat history:', error);
  }
}
